import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { formatEur, formatPct } from '../../helpers/kpiDefinitions';
import { tokens } from '../../theme';
import {
  fetchConversionRate,
  fetchConversionDistribution,
  fetchProductConversion,
  fetchConversionTimeline,
  fetchConversionTime,
  fetchSignupCohortAnalysis,
  fetchConversionDetails,
  fetchProductConversionDetails,
  fetchDomainRevenue,
  fetchCpaTimeline,
  fetchCpaTimelineWindowed,
  computeConversionTimeSummary,
  ConversionRateData,
  ConversionDistributionData,
  ProductConversionData,
  ConversionTimelineData,
  ConversionTimeData,
  SignupCohortData,
  ConversionDetailData,
  DomainRevenueData,
  CpaTimelineData,
} from '../../helpers/anfoStatisticsData';
import { ResponsiveLine } from '@nivo/line';
import moment from 'moment';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`anfo-tabpanel-${index}`}
      aria-labelledby={`anfo-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `anfo-tab-${index}`,
    'aria-controls': `anfo-tabpanel-${index}`,
  };
}

const AnfoStatisticsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Local date state for dual date filtering
  const [signupStartDate, setSignupStartDate] = useState<Date | null>(
    moment().subtract(1, 'year').toDate(),
  );
  const [signupEndDate, setSignupEndDate] = useState<Date | null>(new Date());
  const [orderStartDate, setOrderStartDate] = useState<Date | null>(
    moment().subtract(1, 'year').toDate(),
  );
  const [orderEndDate, setOrderEndDate] = useState<Date | null>(new Date());

  // State for filter selections
  const [selectedClients, setSelectedClients] = useState<string[]>(['ft']);
  const [selectedAboStatuses, setSelectedAboStatuses] = useState<string[]>(['L', 'W', 'B']);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  // Available filter options
  // 'wh' entfernt 2026-09-09: der Client ist stillgelegt, seine Zeilen in mc_members,
  // mc_member_tags und mc_tag_meta sind entfernt (Sicherung unter
  // untracked/wh-decommission-20260909/). 'am' steht seit seiner Stilllegung am
  // 2026-05-27 im selben Zustand und bleibt vorerst drin — bewusst nicht mitgeaendert.
  const clientOptions = ['ft', 'am', 'be', 'dnv', 'fp', 'gp', 'sw', 'tt'];
  const aboStatusOptions = [
    {
      value: 'V',
      label: 'V - Verweigert',
      description: 'Kunde blockiert (bestehendes Abo oder Kündigung < 90 Tage)',
    },
    {
      value: 'L',
      label: 'L - Laufend',
      description: 'Aktives Abo (Testphase oder bezahlt)',
    },
    {
      value: 'W',
      label: 'W - Widerrufen',
      description: 'Gekündigt oder nicht bezahlt nach Testphase',
    },
    {
      value: 'B',
      label: 'B - Beendet',
      description: 'Abo komplett abgelaufen',
    },
  ];
  // Curated source whitelist — must match ALLOWED_ANFO_SOURCES in
  // node-app/src/routes/anfo-statistics.ts. Sources are CLIENT-SCOPED:
  // Ad-Hoc-News/Boerse-Global = gp only, Stock-World = sw, Trading-Treff = tt.
  // If you select ft + Ad-Hoc-News you'll get 0 rows — switch client first.
  // "Finanztrends" = historic NULL default. "FT" = in-article ad units.
  const sourceOptions = [
    'Boerse-Express',
    'FT',
    'Aktiencheck',
    'Finanztrends',
    'Ad-Hoc-News',
    'Boerse-Global',
    'Stock-World',
    'Trading-Treff',
    'Sharedeals',
    'Adhoc-FT',
    'KME',
    'Finanzennet',
    'Embed-AHN',
    'Finanzmarkt',
    'Boerse-Social',
    'APP-PUSH',
  ];

  // State for all data
  const [conversionRate, setConversionRate] = useState<ConversionRateData | null>(null);
  const [conversionDistribution, setConversionDistribution] = useState<
    ConversionDistributionData[]
  >([]);
  const [productConversion, setProductConversion] = useState<ProductConversionData[]>([]);
  const [conversionTimeline, setConversionTimeline] = useState<ConversionTimelineData[]>([]);
  const [conversionTimeData, setConversionTimeData] = useState<ConversionTimeData[]>([]);
  const [signupCohortData, setSignupCohortData] = useState<SignupCohortData[]>([]);
  const [domainRevenueData, setDomainRevenueData] = useState<DomainRevenueData[]>([]);
  const [cpaTimeline, setCpaTimeline] = useState<CpaTimelineData[]>([]);
  const [cpaWindowData, setCpaWindowData] = useState<CpaTimelineData[]>([]);
  const [cpaWindowDays, setCpaWindowDays] = useState<number>(30);
  const [cpaWindowLoading, setCpaWindowLoading] = useState(false);

  // Dialog state for conversion details
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<string>('');
  const [bucketDetails, setBucketDetails] = useState<ConversionDetailData[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Dialog state for product details
  const [productDetailsDialogOpen, setProductDetailsDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{
    code: string;
    name: string;
  }>({ code: '', name: '' });
  const [productDetails, setProductDetails] = useState<ConversionDetailData[]>([]);
  const [loadingProductDetails, setLoadingProductDetails] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load data function (can be called manually or on mount)
  const loadData = async () => {
    // Validate dates before loading
    const dateValidationError = validateDates();
    if (dateValidationError) {
      alert(dateValidationError);
      return;
    }

    try {
      setLoading(true);
      const signupStartDateStr = signupStartDate
        ? moment(signupStartDate).format('YYYY-MM-DD')
        : undefined;
      const signupEndDateStr = signupEndDate
        ? moment(signupEndDate).format('YYYY-MM-DD')
        : undefined;
      const orderStartDateStr = orderStartDate
        ? moment(orderStartDate).format('YYYY-MM-DD')
        : undefined;
      const orderEndDateStr = orderEndDate ? moment(orderEndDate).format('YYYY-MM-DD') : undefined;

      const results = await Promise.allSettled([
        fetchConversionRate(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchConversionDistribution(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchProductConversion(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchConversionTimeline(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchConversionTime(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchSignupCohortAnalysis(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchDomainRevenue(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchCpaTimeline(
          signupStartDateStr,
          signupEndDateStr,
          orderStartDateStr,
          orderEndDateStr,
          selectedClients,
          selectedAboStatuses,
          selectedSources,
        ),
        fetchCpaTimelineWindowed(
          signupStartDateStr,
          signupEndDateStr,
          selectedClients,
          selectedAboStatuses,
          cpaWindowDays,
          selectedSources,
        ),
      ]);

      // Process successful results
      if (results[0].status === 'fulfilled') setConversionRate(results[0].value);
      if (results[1].status === 'fulfilled') setConversionDistribution(results[1].value);
      if (results[2].status === 'fulfilled') setProductConversion(results[2].value);
      if (results[3].status === 'fulfilled') setConversionTimeline(results[3].value);
      if (results[4].status === 'fulfilled') setConversionTimeData(results[4].value);
      if (results[5].status === 'fulfilled') setSignupCohortData(results[5].value);
      if (results[6].status === 'fulfilled') setDomainRevenueData(results[6].value);
      if (results[7].status === 'fulfilled') setCpaTimeline(results[7].value);
      if (results[8].status === 'fulfilled') setCpaWindowData(results[8].value);

      // Log failed requests for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = [
            'conversion-rate',
            'conversion-distribution',
            'product-conversion',
            'conversion-timeline',
            'conversion-time',
            'signup-cohort-analysis',
            'domain-revenue',
            'cpa-timeline',
            'cpa-timeline-windowed',
          ];
          console.error(`Failed to load data for ${endpoints[index]}:`, result.reason);
        }
      });
    } catch (error) {
      console.error('Error loading Anfo statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Date validation function
  const validateDates = (): string | null => {
    const dates = [
      { date: signupStartDate, name: 'Anmeldedatum Start' },
      { date: signupEndDate, name: 'Anmeldedatum End' },
      { date: orderStartDate, name: 'Bestelldatum Start' },
      { date: orderEndDate, name: 'Bestelldatum End' },
    ];

    for (const { date, name } of dates) {
      if (!date) continue;

      const year = date.getFullYear();
      if (year < 1900 || year > 2100) {
        return `${name}: Ungültiges Jahr (${year}). Bitte ein Jahr zwischen 1900 und 2100 eingeben.`;
      }
    }

    // Validate date ranges
    if (signupStartDate && signupEndDate && signupStartDate > signupEndDate) {
      return 'Anmeldedatum Start muss vor Anmeldedatum End liegen.';
    }
    if (orderStartDate && orderEndDate && orderStartDate > orderEndDate) {
      return 'Bestelldatum Start muss vor Bestelldatum End liegen.';
    }

    return null;
  };

  // Load data only on mount
  useEffect(() => {
    loadData();
  }, []); // Empty dependency array - only runs on mount

  // Re-fetch windowed CPA data when days selector changes (skip initial mount — handled by loadData)
  const [cpaWindowMounted, setCpaWindowMounted] = useState(false);
  useEffect(() => {
    if (!cpaWindowMounted) {
      setCpaWindowMounted(true);
      return;
    }
    let cancelled = false;
    const refetch = async () => {
      setCpaWindowLoading(true);
      try {
        const signupStartDateStr = signupStartDate
          ? moment(signupStartDate).format('YYYY-MM-DD')
          : undefined;
        const signupEndDateStr = signupEndDate
          ? moment(signupEndDate).format('YYYY-MM-DD')
          : undefined;
        const data = await fetchCpaTimelineWindowed(
          signupStartDateStr,
          signupEndDateStr,
          selectedClients,
          selectedAboStatuses,
          cpaWindowDays,
          selectedSources,
        );
        if (!cancelled) setCpaWindowData(data);
      } catch (error) {
        console.error('Error fetching windowed CPA data:', error);
      } finally {
        if (!cancelled) setCpaWindowLoading(false);
      }
    };
    refetch();
    return () => {
      cancelled = true;
    };
  }, [cpaWindowDays]); // Only re-fetch when days changes

  // Calculate time summary
  const timeSummary = computeConversionTimeSummary(conversionTimeData);

  // Handle row click for conversion details
  const handleRowClick = async (timeBucket: string) => {
    if (timeBucket === 'Nicht konvertiert') {
      return; // Don't allow clicking on "Nicht konvertiert" row
    }

    setSelectedBucket(timeBucket);
    setDetailsDialogOpen(true);
    setLoadingDetails(true);
    setBucketDetails([]);

    try {
      const signupStartDateStr = signupStartDate
        ? moment(signupStartDate).format('YYYY-MM-DD')
        : undefined;
      const signupEndDateStr = signupEndDate
        ? moment(signupEndDate).format('YYYY-MM-DD')
        : undefined;
      const orderStartDateStr = orderStartDate
        ? moment(orderStartDate).format('YYYY-MM-DD')
        : undefined;
      const orderEndDateStr = orderEndDate ? moment(orderEndDate).format('YYYY-MM-DD') : undefined;

      const details = await fetchConversionDetails(
        timeBucket,
        signupStartDateStr,
        signupEndDateStr,
        orderStartDateStr,
        orderEndDateStr,
        selectedClients,
        selectedAboStatuses,
        selectedSources,
      );
      setBucketDetails(details);
    } catch (error) {
      console.error('Error fetching conversion details:', error);
      setBucketDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedBucket('');
    setBucketDetails([]);
  };

  // Handle product row click for product details
  const handleProductRowClick = async (productCode: string, productName: string) => {
    setSelectedProduct({ code: productCode, name: productName });
    setProductDetailsDialogOpen(true);
    setLoadingProductDetails(true);
    setProductDetails([]);

    try {
      const signupStartDateStr = signupStartDate
        ? moment(signupStartDate).format('YYYY-MM-DD')
        : undefined;
      const signupEndDateStr = signupEndDate
        ? moment(signupEndDate).format('YYYY-MM-DD')
        : undefined;
      const orderStartDateStr = orderStartDate
        ? moment(orderStartDate).format('YYYY-MM-DD')
        : undefined;
      const orderEndDateStr = orderEndDate ? moment(orderEndDate).format('YYYY-MM-DD') : undefined;

      const details = await fetchProductConversionDetails(
        productCode,
        signupStartDateStr,
        signupEndDateStr,
        orderStartDateStr,
        orderEndDateStr,
        selectedClients,
        selectedAboStatuses,
        selectedSources,
      );
      setProductDetails(details);
    } catch (error) {
      console.error('Error fetching product conversion details:', error);
      setProductDetails([]);
    } finally {
      setLoadingProductDetails(false);
    }
  };

  const handleCloseProductDialog = () => {
    setProductDetailsDialogOpen(false);
    setSelectedProduct({ code: '', name: '' });
    setProductDetails([]);
  };

  // Chart data for timeline with normalized dual-axis approach
  const chartData =
    conversionTimeline.length > 0
      ? (() => {
          // Find max values for normalization
          const maxSignups = Math.max(...conversionTimeline.map((item) => item.new_signups));
          const maxConversions = Math.max(...conversionTimeline.map((item) => item.conversions));
          const maxRevenue = Math.max(...conversionTimeline.map((item) => item.revenue || 0));

          // Normalize conversions and revenue to signup scale
          const conversionsNormalizationFactor = (maxSignups / maxConversions) * 0.3; // Scale to 30% of signup range
          const revenueNormalizationFactor = (maxSignups / maxRevenue) * 0.25; // Scale to 25% of signup range

          return [
            {
              id: 'Neue Anmeldungen',
              data: conversionTimeline.map((item) => ({
                x: item.month,
                y: item.new_signups,
                originalValue: item.new_signups,
              })),
            },
            {
              id: 'Konvertierungen',
              data: conversionTimeline.map((item) => ({
                x: item.month,
                y: item.conversions * conversionsNormalizationFactor,
                originalValue: item.conversions,
              })),
            },
            {
              id: 'Umsatz (€)',
              data: conversionTimeline.map((item) => ({
                x: item.month,
                y: (item.revenue || 0) * revenueNormalizationFactor,
                originalValue: item.revenue || 0,
              })),
            },
          ];
        })()
      : [];

  // CPA timeline chart data with dual-axis normalization
  const cpaChartData = useMemo(() => {
    if (cpaTimeline.length === 0) return [];

    const maxCpaPerSignup = Math.max(
      ...cpaTimeline.map((item) => Number(item.cpa_per_signup) || 0),
    );
    const maxSignups = Math.max(...cpaTimeline.map((item) => Number(item.new_signups) || 0));
    const maxTotalCpa = Math.max(...cpaTimeline.map((item) => Number(item.total_cpa) || 0));

    if (maxCpaPerSignup === 0) return [];

    return [
      {
        id: 'CPA / E-Mail (€)',
        data: cpaTimeline.map((item) => ({
          x: item.month,
          y: Number(item.cpa_per_signup) || 0,
          originalValue: Number(item.cpa_per_signup) || 0,
        })),
      },
      {
        id: 'Neue Anmeldungen',
        data: cpaTimeline.map((item) => {
          const signups = Number(item.new_signups) || 0;
          return {
            x: item.month,
            y: maxSignups > 0 ? (signups / maxSignups) * maxCpaPerSignup : 0,
            originalValue: signups,
          };
        }),
      },
      {
        id: 'CPA-Wert (€)',
        data: cpaTimeline.map((item) => {
          const totalCpa = Number(item.total_cpa) || 0;
          return {
            x: item.month,
            y: maxTotalCpa > 0 ? (totalCpa / maxTotalCpa) * maxCpaPerSignup * 0.8 : 0,
            originalValue: totalCpa,
          };
        }),
      },
    ];
  }, [cpaTimeline]);

  // Max signups for right axis denormalization
  const cpaChartMaxSignups = useMemo(() => {
    if (cpaTimeline.length === 0) return 0;
    return Math.max(...cpaTimeline.map((item) => Number(item.new_signups) || 0));
  }, [cpaTimeline]);

  const cpaChartMaxCpaPerSignup = useMemo(() => {
    if (cpaTimeline.length === 0) return 0;
    return Math.max(...cpaTimeline.map((item) => Number(item.cpa_per_signup) || 0));
  }, [cpaTimeline]);

  // Windowed CPA chart data — same normalization as existing CPA chart
  const cpaWindowChartData = useMemo(() => {
    if (cpaWindowData.length === 0) return [];

    const maxCpaPerSignup = Math.max(
      ...cpaWindowData.map((item) => Number(item.cpa_per_signup) || 0),
    );
    const maxSignups = Math.max(...cpaWindowData.map((item) => Number(item.new_signups) || 0));
    const maxTotalCpa = Math.max(...cpaWindowData.map((item) => Number(item.total_cpa) || 0));

    if (maxCpaPerSignup === 0) return [];

    return [
      {
        id: 'CPA / E-Mail (€)',
        data: cpaWindowData.map((item) => ({
          x: item.month,
          y: Number(item.cpa_per_signup) || 0,
          originalValue: Number(item.cpa_per_signup) || 0,
        })),
      },
      {
        id: 'Neue Anmeldungen',
        data: cpaWindowData.map((item) => {
          const signups = Number(item.new_signups) || 0;
          return {
            x: item.month,
            y: maxSignups > 0 ? (signups / maxSignups) * maxCpaPerSignup : 0,
            originalValue: signups,
          };
        }),
      },
      {
        id: 'CPA-Wert (€)',
        data: cpaWindowData.map((item) => {
          const totalCpa = Number(item.total_cpa) || 0;
          return {
            x: item.month,
            y: maxTotalCpa > 0 ? (totalCpa / maxTotalCpa) * maxCpaPerSignup * 0.8 : 0,
            originalValue: totalCpa,
          };
        }),
      },
    ];
  }, [cpaWindowData]);

  const cpaWindowMaxSignups = useMemo(() => {
    if (cpaWindowData.length === 0) return 0;
    return Math.max(...cpaWindowData.map((item) => Number(item.new_signups) || 0));
  }, [cpaWindowData]);

  const cpaWindowMaxCpaPerSignup = useMemo(() => {
    if (cpaWindowData.length === 0) return 0;
    return Math.max(...cpaWindowData.map((item) => Number(item.cpa_per_signup) || 0));
  }, [cpaWindowData]);

  // Summary KPIs for windowed CPA
  const cpaWindowSummary = useMemo(() => {
    if (cpaWindowData.length === 0) return { avgCpa: 0, totalSignups: 0, totalCpa: 0 };
    const totalSignups = cpaWindowData.reduce(
      (sum, item) => sum + (Number(item.new_signups) || 0),
      0,
    );
    const totalCpa = cpaWindowData.reduce((sum, item) => sum + (Number(item.total_cpa) || 0), 0);
    return {
      avgCpa: totalSignups > 0 ? totalCpa / totalSignups : 0,
      totalSignups,
      totalCpa,
    };
  }, [cpaWindowData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Explanation Box */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.blueAccent[800],
          border: `1px solid ${colors.blueAccent[600]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoOutlined sx={{ color: colors.blueAccent[400], fontSize: 24, mt: 0.5 }} />
          <Box>
            <Typography variant="h6" sx={{ color: colors.blueAccent[400], mb: 1 }}>
              ANFO Conversion-Analyse: Dual-Date Filtering erklärt
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[100], lineHeight: 1.6 }}>
              <strong>Diese Statistik nutzt zwei separate Zeitraum-Filter:</strong>
              <br />• <strong>Anmeldedatum-Filter:</strong> Für Conversion-Metriken (Gesamte
              E-Mails, Konvertierte E-Mails, Conversion-Rate)
              <br />• <strong>Bestelldatum-Filter:</strong> Für Umsatz-Metriken (Gesamtumsatz, Ø
              Umsatz pro E-Mail, Ø Umsatz pro Kunde)
              <br />• <strong>Vorteil:</strong> Getrennte Analyse möglich - z.B. &quot;Welchen
              Umsatz haben Anmeldungen aus Q1 in Q3 generiert?&quot;
              <br />• <strong>Standard-Verwendung:</strong> Beide Zeiträume identisch setzen für
              synchrone Analyse
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Gesamte E-Mails
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Anzahl einzigartiger E-Mail-Adressen im Anmeldezeitraum</strong>
                      <br />
                      Formel: COUNT(DISTINCT email)
                      <br />
                      Filtert nach: <strong>Anmeldedatum</strong> (lfa.date)
                      <br />
                      Beispiel: 51.476 E-Mails haben sich zwischen 2024-11-17 und 2025-11-17
                      erstmals angemeldet
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.total_emails?.toLocaleString('de-DE') || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Konvertierte E-Mails
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>E-Mails aus Anmeldezeitraum mit mindestens einer Bestellung</strong>
                      <br />
                      Formel: COUNT(DISTINCT email WHERE MIN(order_date) IS NOT NULL)
                      <br />
                      Filtert nach: <strong>Anmeldedatum</strong> (lfa.date), prüft Bestellungen zu
                      beliebigem Zeitpunkt
                      <br />
                      Beispiel: 395 der 51.476 Anmeldungen haben irgendwann eine Bestellung
                      aufgegeben
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.converted_emails?.toLocaleString('de-DE') || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Gesamte Bestellungen
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Gesamtanzahl aller Bestellungen (nicht nur Erstbestellungen)</strong>
                      <br />
                      Formel: COUNT(DISTINCT ordernumber)
                      <br />
                      Filtert nach: <strong>Anmeldedatum</strong> (lfa.date), zählt alle
                      Bestellungen dieser E-Mails
                      <br />
                      Hinweis: Ein Nutzer kann mehrere Bestellungen haben
                      <br />
                      Beispiel: 1.903 Bestellungen von den 395 konvertierten E-Mails (Ø 4,8
                      Bestellungen/Kunde)
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.total_orders?.toLocaleString('de-DE') || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Conversion-Rate
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Prozentsatz der Anmeldungen, die jemals konvertierten</strong>
                      <br />
                      Formel: (Konvertierte E-Mails ÷ Gesamte E-Mails) × 100
                      <br />
                      Filtert nach: <strong>Anmeldedatum</strong> (lfa.date)
                      <br />
                      Bedeutung: Anteil der Anmeldungen, die später bestellt haben
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {formatPct(Number(conversionRate?.conversion_rate || 0), 2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Ø Tage bis Bestellung
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Durchschnittliche Zeit von Anmeldung bis Erstbestellung</strong>
                      <br />
                      Formel: AVG(DATEDIFF(MIN(order_date), signup_date))
                      <br />
                      Filtert nach: <strong>Anmeldedatum</strong> (lfa.date)
                      <br />
                      Nur für konvertierte E-Mails berechnet
                      <br />
                      Beispiel: Durchschnittlich vergehen 45 Tage zwischen Anmeldung und erster
                      Bestellung
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {Number(timeSummary.averageDays).toLocaleString('de-DE', { maximumFractionDigits: 1 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Gesamtumsatz
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Tatsächlich erhaltener Umsatz aus Bestellungen</strong>
                      <br />
                      Formel: SUM(plx_order_metrics.actual_revenue)
                      <br />
                      Filtert nach: <strong>Bestelldatum</strong> (pao.created_on) - NICHT
                      Anmeldedatum!
                      <br />
                      Quelle: plx_order_metrics (gecachte Zahlungsdaten)
                      <br />
                      Abo-Status: Filterbar über Dropdown (Standard: L, W, B - ohne V)
                      <br />
                      Beispiel: €231.854,08 Umsatz aus Bestellungen zwischen 2024-11-17 und
                      2025-11-17
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.total_revenue
                  ? formatEur(Number(conversionRate.total_revenue))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Ø Umsatz pro E-Mail
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Durchschnittlicher Umsatz pro Anmeldung (inkl. Nicht-Käufer)</strong>
                      <br />
                      Formel: Gesamtumsatz ÷ Gesamte E-Mails
                      <br />
                      Filtert: Umsatz nach <strong>Bestelldatum</strong>, E-Mails nach{' '}
                      <strong>Anmeldedatum</strong>
                      <br />
                      Bedeutung: bezahlter Umsatz je Anmeldung — egal, ob sie kauft oder nicht
                      <br />
                      Wichtig für ROI-Kalkulation von Marketing-Kampagnen
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.avg_revenue_per_signup
                  ? formatEur(Number(conversionRate.avg_revenue_per_signup))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Ø Umsatz pro Kunde
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Durchschnittlicher Umsatz pro zahlenden Kunden</strong>
                      <br />
                      Formel: Gesamtumsatz ÷ E-Mails mit Umsatz
                      <br />
                      Filtert: Umsatz nach <strong>Bestelldatum</strong>, zählt nur E-Mails mit
                      tatsächlichem Umsatz
                      <br />
                      Bedeutung: was ein zahlender Kunde im Bestellzeitraum durchschnittlich gebracht
                      hat. Nicht der Anfo-Wert (Prognose) und nicht der Wert je FT-Anmeldung (eigene Seite).
                      <br />
                      Hinweis: Höher als Ø pro E-Mail, da nur Käufer gezählt werden
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.avg_revenue_per_customer
                  ? formatEur(Number(conversionRate.avg_revenue_per_customer))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Ø Umsatz pro Bestellung
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Durchschnittlicher Bestellwert (Average Order Value)</strong>
                      <br />
                      Formel: Gesamtumsatz ÷ Bestellungen mit einer Zahlung über 1 €
                      <br />
                      Filtert nach: <strong>Bestelldatum</strong>
                      <br />
                      Bedeutung: Was eine einzelne Bestellung durchschnittlich einbringt
                      <br />
                      Hinweis: Niedriger als Ø pro Kunde, da Kunden oft mehrere Bestellungen haben
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.avg_revenue_per_order
                  ? formatEur(Number(conversionRate.avg_revenue_per_order))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Umsatzrelevante CR
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Conversion-Rate für Umsatz-generierende Bestellungen</strong>
                      <br />
                      Formel: (E-Mails mit Umsatz ÷ Gesamte E-Mails) × 100
                      <br />
                      Filtert: E-Mails nach <strong>Anmeldedatum</strong>, Umsatz nach{' '}
                      <strong>Bestelldatum</strong>
                      <br />
                      Bedeutung: Anteil der Anmeldungen, die im Bestellzeitraum tatsächlich Geld
                      gebracht haben
                      <br />
                      Zahlung heißt über 1 € — eine laufende Testphase trägt eine Rechnung über 0,99
                      € und zählt nicht als Zahler
                      <br />
                      Liegt unter der normalen Conversion-Rate: die zählt jede Bestellung, diese nur
                      die bezahlten
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.greenAccent[400]}>
                {conversionRate?.revenue_conversion_rate
                  ? formatPct(Number(conversionRate.revenue_conversion_rate), 2)
                  : formatPct(0, 2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expected Revenue KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ backgroundColor: colors.blueAccent[700] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Anfo-Wert
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>
                        Erwarteter Umsatz der Bestellungen über 36 Monate, brutto (inkl. MwSt.)
                      </strong>
                      <br />
                      Formel: Summe der Anfo-Werte aller Bestellungen (je Bestellung: Wert ihrer
                      Zahlung, sonst ihres Angebots, sonst ihres Produkts)
                      <br />
                      Bedeutung: Potenzial aller Bestellungen — Prognose, kein Zahlungseingang
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.blueAccent[300]}>
                {conversionRate?.expected_revenue
                  ? formatEur(Number(conversionRate.expected_revenue))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ backgroundColor: colors.blueAccent[700] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Erw. Ø / E-Mail
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Anfo-Wert pro E-Mail-Anmeldung</strong>
                      <br />
                      Formel: Anfo-Wert ÷ Gesamte E-Mails
                      <br />
                      Bedeutung: Prognostizierter Wert pro Anmeldung
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.blueAccent[300]}>
                {conversionRate?.expected_avg_per_signup
                  ? formatEur(Number(conversionRate.expected_avg_per_signup))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ backgroundColor: colors.blueAccent[700] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Erw. Ø / Besteller
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Anfo-Wert je E-Mail mit Bestellung</strong>
                      <br />
                      Formel: Anfo-Wert ÷ E-Mails mit Bestellung
                      <br />
                      Bedeutung: der Anfo-Wert, verteilt auf alle Besteller — nicht nur auf die,
                      die schon gezahlt haben. Der Anfo-Wert wird je Bestellung
                      angesetzt, der Nenner muss deshalb auch jede Bestellung enthalten.
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.blueAccent[300]}>
                {conversionRate?.expected_avg_per_customer
                  ? formatEur(Number(conversionRate.expected_avg_per_customer))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CPA KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ backgroundColor: colors.blueAccent[700] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                CPA-Wert
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>
                        CPA-Wert — jede Bestellung mit ihrem eigenen CPA-Wert bewertet
                      </strong>
                      <br />
                      Formel: Summe der CPA-Werte aller Bestellungen (je Bestellung: Wert ihrer
                      Zahlung, sonst ihres Angebots, sonst ihres Produkts; 0, wenn keiner vorliegt)
                      <br />
                      Kein Zahlungseingang — die Steuerungsgröße für Kampagnen (seit 16.09.2026 ohne
                      Rückfall auf den Anfo-Wert)
                      <br />
                      Bedeutung: Akquise-Budget, das diese Bestellungen wert sind — keine Ist-Kosten
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.blueAccent[300]}>
                {conversionRate?.total_cpa
                  ? formatEur(Number(conversionRate.total_cpa))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ backgroundColor: colors.blueAccent[700] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                CPA-Wert / E-Mail
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>CPA-Wert pro E-Mail-Anmeldung</strong>
                      <br />
                      Formel: CPA-Wert ÷ Gesamte E-Mails
                      <br />
                      Bedeutung: Durchschnittlicher CPA-Wert pro Anmeldung (inkl. Nicht-Käufer)
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.blueAccent[300]}>
                {conversionRate?.cpa_per_signup
                  ? formatEur(Number(conversionRate.cpa_per_signup))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ backgroundColor: colors.blueAccent[700] }}>
            <CardContent>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                CPA-Wert / Besteller
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>CPA-Wert je E-Mail mit Bestellung</strong>
                      <br />
                      Formel: CPA-Wert ÷ E-Mails mit Bestellung
                      <br />
                      Bedeutung: der angesetzte Akquise-Wert, verteilt auf alle Besteller. Er fällt
                      je Bestellung an, nicht je Zahler — der Nenner enthält deshalb auch
                      Bestellungen ohne Zahlungseingang.
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Typography variant="h3" fontWeight="600" color={colors.blueAccent[300]}>
                {conversionRate?.cpa_per_customer
                  ? formatEur(Number(conversionRate.cpa_per_customer))
                  : formatEur(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Controls */}
      <Box mb={3} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Anmeldedatum Start"
          type="date"
          value={signupStartDate ? moment(signupStartDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setSignupStartDate(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />

        <TextField
          label="Anmeldedatum End"
          type="date"
          value={signupEndDate ? moment(signupEndDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setSignupEndDate(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />

        <TextField
          label="Bestelldatum Start"
          type="date"
          value={orderStartDate ? moment(orderStartDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setOrderStartDate(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />

        <TextField
          label="Bestelldatum End"
          type="date"
          value={orderEndDate ? moment(orderEndDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => setOrderEndDate(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="client-select-label">Client</InputLabel>
          <Select
            labelId="client-select-label"
            id="client-select"
            multiple
            value={selectedClients}
            onChange={(event) => {
              const value =
                typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : event.target.value;
              setSelectedClients(value);
            }}
            input={<OutlinedInput id="select-multiple-client-chip" label="Client" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value.toUpperCase()} size="small" />
                ))}
              </Box>
            )}
          >
            {clientOptions.map((client) => (
              <MenuItem key={client} value={client}>
                {client.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel id="abo-status-select-label">Abo-Status</InputLabel>
          <Select
            labelId="abo-status-select-label"
            id="abo-status-select"
            multiple
            value={selectedAboStatuses}
            onChange={(event) => {
              const value =
                typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : event.target.value;
              setSelectedAboStatuses(value);
            }}
            input={<OutlinedInput id="select-multiple-abo-status-chip" label="Abo-Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {aboStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Tooltip title={option.description} arrow>
                  <span>{option.label}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="source-select-label">Quelle</InputLabel>
          <Select
            labelId="source-select-label"
            id="source-select"
            multiple
            value={selectedSources}
            onChange={(event) => {
              const value =
                typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : event.target.value;
              setSelectedSources(value);
            }}
            input={<OutlinedInput id="select-multiple-source-chip" label="Quelle" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {sourceOptions.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={loadData}
          disabled={loading}
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: '14px',
            fontWeight: 'bold',
            padding: '10px 20px',
            minWidth: 140,
            '&:hover': {
              backgroundColor: colors.blueAccent[800],
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : 'Aktualisieren'}
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="anfo statistics tabs">
          <Tab label="Zeitverteilung" {...a11yProps(0)} />
          <Tab label="Produkte" {...a11yProps(1)} />
          <Tab label="Zeitverlauf" {...a11yProps(2)} />
          <Tab label="E-Mail Domains" {...a11yProps(3)} />
          <Tab label="CPA-Fenster" {...a11yProps(4)} />
        </Tabs>
      </Box>

      {/* Tab 1: Time Distribution */}
      <TabPanel value={tabValue} index={0}>
        {!loading && (
          <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
            <Typography
              variant="h5"
              mb={2}
              color={colors.grey[100]}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              Verteilung der Konvertierungszeiten
              <Tooltip title="Unified view: Zeigt sowohl die Verteilung der Konvertierungszeiten als auch den Anteil an allen Anmeldungen im Zeitraum">
                <InfoOutlined
                  sx={{
                    fontSize: 16,
                    ml: 1,
                    color: colors.grey[300],
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            </Typography>
            {conversionDistribution &&
              conversionDistribution.length > 0 &&
              signupCohortData &&
              signupCohortData.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          Zeitraum
                          <Tooltip title="Zeitspanne zwischen Anmeldung und erster Bestellung">
                            <InfoOutlined
                              sx={{
                                fontSize: 14,
                                ml: 1,
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          Anzahl
                          <Tooltip title="Anzahl der Konvertierungen in diesem Zeitraum">
                            <InfoOutlined
                              sx={{
                                fontSize: 14,
                                ml: 1,
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          Anteil Konvertierer (%)
                          <Tooltip title="Prozentanteil dieser Kategorie an allen Konvertierungen">
                            <InfoOutlined
                              sx={{
                                fontSize: 14,
                                ml: 1,
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          Anteil an Gesamt-Anmeldungen (%)
                          <Tooltip title="Prozentanteil dieser Kategorie an allen Anmeldungen im Zeitraum">
                            <InfoOutlined
                              sx={{
                                fontSize: 14,
                                ml: 1,
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Create unified data by merging both datasets
                        const unifiedData: Array<{
                          time_bucket: string;
                          count: number;
                          converter_percentage: number;
                          total_percentage: number;
                          isSpecial: boolean;
                        }> = [];

                        // Add conversion distribution data
                        conversionDistribution.forEach((row) => {
                          const cohortRow = signupCohortData.find(
                            (c) => c.time_bucket === row.time_bucket,
                          );
                          unifiedData.push({
                            time_bucket: row.time_bucket,
                            count: row.count,
                            converter_percentage: row.percentage,
                            total_percentage: cohortRow ? cohortRow.percentage : 0,
                            isSpecial: row.time_bucket === 'Zeitliches Matching nicht möglich',
                          });
                        });

                        // Add "Nicht konvertiert" from cohort data
                        const notConvertedRow = signupCohortData.find(
                          (c) => c.time_bucket === 'Nicht konvertiert',
                        );
                        if (notConvertedRow) {
                          unifiedData.push({
                            time_bucket: notConvertedRow.time_bucket,
                            count: notConvertedRow.signup_count,
                            converter_percentage: 0,
                            total_percentage: notConvertedRow.percentage,
                            isSpecial: true,
                          });
                        }

                        return unifiedData.map((row, index) => (
                          <TableRow
                            key={index}
                            onClick={() => handleRowClick(row.time_bucket)}
                            sx={{
                              backgroundColor:
                                row.time_bucket === 'Nicht konvertiert' ||
                                row.time_bucket === 'Zeitliches Matching nicht möglich'
                                  ? colors.redAccent[800]
                                  : 'inherit',
                              cursor:
                                row.time_bucket !== 'Nicht konvertiert' ? 'pointer' : 'default',
                              '&:hover':
                                row.time_bucket !== 'Nicht konvertiert'
                                  ? {
                                      backgroundColor: colors.primary[600],
                                    }
                                  : {},
                            }}
                          >
                            <TableCell>{row.time_bucket}</TableCell>
                            <TableCell align="right">{row.count.toLocaleString('de-DE')}</TableCell>
                            <TableCell align="right">
                              {row.converter_percentage > 0 ? `${row.converter_percentage}%` : '-'}
                            </TableCell>
                            <TableCell align="right">{row.total_percentage}%</TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

            <Grid container spacing={3} mt={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[100]}>
                      Minimum
                    </Typography>
                    <Typography variant="h5" color={colors.greenAccent[400]}>
                      {timeSummary.minDays} Tage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[100]}>
                      Median
                    </Typography>
                    <Typography variant="h5" color={colors.greenAccent[400]}>
                      {timeSummary.medianDays} Tage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[100]}>
                      Durchschnitt
                    </Typography>
                    <Typography variant="h5" color={colors.greenAccent[400]}>
                      {timeSummary.averageDays} Tage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: colors.blueAccent[800] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[100]}>
                      Maximum
                    </Typography>
                    <Typography variant="h5" color={colors.greenAccent[400]}>
                      {timeSummary.maxDays} Tage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}
      </TabPanel>

      {/* Tab 2: Products */}
      <TabPanel value={tabValue} index={1}>
        {!loading && (
          <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
            <Typography variant="h5" mb={2} color={colors.grey[100]}>
              Konvertierungen nach Produkt
            </Typography>
            {productConversion && productConversion.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produktcode</TableCell>
                      <TableCell>Produktname</TableCell>
                      <TableCell align="right">Konvertierte E-Mails</TableCell>
                      <TableCell align="right">Gesamtbestellungen</TableCell>
                      <TableCell align="right">Ø Tage bis Konvertierung</TableCell>
                      <TableCell align="right">Conversion-Rate (%)</TableCell>
                      <TableCell align="right">Gesamtumsatz (€)</TableCell>
                      <TableCell align="right">Ø Umsatz/Bestellung (€)</TableCell>
                      <TableCell align="right">CPA-Wert (€)</TableCell>
                      <TableCell align="right">Ø CPA-Wert/Bestellung (€)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productConversion.map((row, index) => (
                      <TableRow
                        key={index}
                        onClick={() => handleProductRowClick(row.product_code, row.product_name)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: colors.primary[600],
                          },
                        }}
                      >
                        <TableCell>{row.product_code}</TableCell>
                        <TableCell>{row.product_name}</TableCell>
                        <TableCell align="right">
                          {row.converted_emails.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {row.total_orders.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">{row.avg_days_to_convert}</TableCell>
                        <TableCell align="right">
                          {Number(row.conversion_rate).toFixed(3)}%
                        </TableCell>
                        <TableCell align="right">
                          {row.total_revenue?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                        <TableCell align="right">
                          {row.avg_revenue_per_order?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                        <TableCell align="right">
                          {row.total_cpa?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                        <TableCell align="right">
                          {row.avg_cpa_per_order?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Tab 3: Timeline */}
      <TabPanel value={tabValue} index={2}>
        {!loading && (
          <>
            <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography variant="h5" mb={2} color={colors.grey[100]}>
                Konvertierungsverlauf über Zeit
              </Typography>
              <Box height="400px" mt={2}>
                {chartData.length > 0 && (
                  <ResponsiveLine
                    data={chartData}
                    theme={{
                      axis: {
                        domain: {
                          line: {
                            stroke: colors.grey[100],
                          },
                        },
                        legend: {
                          text: {
                            fill: colors.grey[100],
                          },
                        },
                        ticks: {
                          line: {
                            stroke: colors.grey[100],
                            strokeWidth: 1,
                          },
                          text: {
                            fill: colors.grey[100],
                          },
                        },
                      },
                      legends: {
                        text: {
                          fill: colors.grey[100],
                        },
                      },
                      tooltip: {
                        container: {
                          color: colors.primary[500],
                        },
                      },
                    }}
                    colors={[
                      colors.greenAccent[500],
                      colors.blueAccent[500],
                      colors.redAccent[500],
                    ]}
                    margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: 'auto',
                      max: 'auto',
                      stacked: false,
                      reverse: false,
                    }}
                    tooltip={({ point }) => {
                      const originalValue = (point.data as any).originalValue;
                      const formattedValue =
                        point.serieId === 'Umsatz (€)'
                          ? `€${originalValue?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : originalValue?.toLocaleString('de-DE');

                      return (
                        <div
                          style={{
                            background: colors.primary[400],
                            padding: '9px 12px',
                            border: `1px solid ${colors.grey[100]}`,
                            borderRadius: '4px',
                            color: colors.grey[100],
                          }}
                        >
                          <strong>{point.serieId}</strong>
                          <br />
                          Monat: {point.data.xFormatted}
                          <br />
                          {point.serieId === 'Umsatz (€)' ? 'Betrag' : 'Anzahl'}:{' '}
                          {formattedValue || point.data.yFormatted}
                          {(point.serieId === 'Konvertierungen' ||
                            point.serieId === 'Umsatz (€)') && (
                            <div
                              style={{
                                fontSize: '0.8em',
                                opacity: 0.8,
                                marginTop: '4px',
                              }}
                            >
                              (Skaliert für Visualisierung)
                            </div>
                          )}
                        </div>
                      );
                    }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Monat',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'Anzahl',
                      legendOffset: -50,
                      legendPosition: 'middle',
                    }}
                    pointSize={10}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={true}
                    legends={[
                      {
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 100,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 120,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemBackground: 'rgba(0, 0, 0, .03)',
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                  />
                )}
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 2, mt: 3, backgroundColor: colors.primary[400] }}>
              <Typography
                variant="h5"
                mb={2}
                color={colors.grey[100]}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                CPA-Wert / E-Mail Entwicklung
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>
                        CPA-Wert pro E-Mail gruppiert nach Anmeldemonat (Kohortenanalyse)
                      </strong>
                      <br />
                      Zeigt den durchschnittlichen CPA-Wert pro Anmeldung für jede monatliche
                      Kohorte.
                      <br />
                      Beispiel: die Zeile Dezember 2025 zeigt, wie viel CPA-Wert die im Dezember
                      angemeldeten E-Mails je Anmeldung bisher gebracht haben.
                      <br />
                      <br />
                      <strong>Unterschied zum Konvertierungsverlauf:</strong> Dort wird nach
                      Bestellmonat gruppiert. Hier wird nach Anmeldemonat gruppiert.
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Box height="400px" mt={2}>
                {cpaChartData.length > 0 && (
                  <ResponsiveLine
                    data={cpaChartData}
                    theme={{
                      axis: {
                        domain: {
                          line: {
                            stroke: colors.grey[100],
                          },
                        },
                        legend: {
                          text: {
                            fill: colors.grey[100],
                          },
                        },
                        ticks: {
                          line: {
                            stroke: colors.grey[100],
                            strokeWidth: 1,
                          },
                          text: {
                            fill: colors.grey[100],
                          },
                        },
                      },
                      legends: {
                        text: {
                          fill: colors.grey[100],
                        },
                      },
                      tooltip: {
                        container: {
                          color: colors.primary[500],
                        },
                      },
                    }}
                    colors={[
                      colors.greenAccent[500],
                      'rgba(141, 179, 227, 0.35)',
                      'rgba(180, 100, 100, 0.35)',
                    ]}
                    lineWidth={3}
                    enableArea={true}
                    areaOpacity={0.08}
                    defs={[
                      {
                        id: 'areaGradientCpa',
                        type: 'linearGradient',
                        colors: [
                          {
                            offset: 0,
                            color: colors.greenAccent[500],
                            opacity: 0.2,
                          },
                          {
                            offset: 100,
                            color: colors.greenAccent[500],
                            opacity: 0,
                          },
                        ],
                      },
                      {
                        id: 'areaHidden',
                        type: 'linearGradient',
                        colors: [
                          { offset: 0, color: '#000', opacity: 0 },
                          { offset: 100, color: '#000', opacity: 0 },
                        ],
                      },
                    ]}
                    fill={[
                      {
                        match: { id: 'CPA / E-Mail (€)' },
                        id: 'areaGradientCpa',
                      },
                      { match: { id: 'Neue Anmeldungen' }, id: 'areaHidden' },
                      { match: { id: 'CPA-Wert (€)' }, id: 'areaHidden' },
                    ]}
                    margin={{ top: 50, right: 140, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto',
                      stacked: false,
                      reverse: false,
                    }}
                    tooltip={({ point }) => {
                      const originalValue = (point.data as any).originalValue;
                      const isCurrency =
                        point.serieId === 'CPA / E-Mail (€)' || point.serieId === 'Gesamt CPA (€)';
                      const formattedValue = isCurrency
                        ? `€${originalValue?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : originalValue?.toLocaleString('de-DE');

                      return (
                        <div
                          style={{
                            background: colors.primary[400],
                            padding: '9px 12px',
                            border: `1px solid ${colors.grey[100]}`,
                            borderRadius: '4px',
                            color: colors.grey[100],
                          }}
                        >
                          <strong>{point.serieId}</strong>
                          <br />
                          Monat: {point.data.xFormatted}
                          <br />
                          {isCurrency ? 'Betrag' : 'Anzahl'}: {formattedValue}
                        </div>
                      );
                    }}
                    axisTop={null}
                    axisRight={
                      cpaChartMaxSignups > 0
                        ? {
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Anmeldungen',
                            legendOffset: 55,
                            legendPosition: 'middle',
                            format: (value: number) => {
                              if (cpaChartMaxCpaPerSignup === 0) return '0';
                              const denormalized = Math.round(
                                (value / cpaChartMaxCpaPerSignup) * cpaChartMaxSignups,
                              );
                              return denormalized.toLocaleString('de-DE');
                            },
                          }
                        : null
                    }
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Anmeldemonat',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'CPA / E-Mail (€)',
                      legendOffset: -50,
                      legendPosition: 'middle',
                      format: (value: number) => `€${value}`,
                    }}
                    pointSize={10}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={true}
                    legends={[
                      {
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 130,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 150,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemBackground: 'rgba(0, 0, 0, .03)',
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                  />
                )}
              </Box>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Tab 4: Email Domains */}
      <TabPanel value={tabValue} index={3}>
        {!loading && (
          <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
            <Typography variant="h5" mb={2} color={colors.grey[100]}>
              Umsatz nach E-Mail Domain
            </Typography>
            {domainRevenueData && domainRevenueData.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>E-Mail Domain</TableCell>
                      <TableCell align="right">Anzahl E-Mails</TableCell>
                      <TableCell align="right">Konvertierte E-Mails</TableCell>
                      <TableCell align="right">Conversion-Rate (%)</TableCell>
                      <TableCell align="right">Gesamtbestellungen</TableCell>
                      <TableCell align="right">Gesamtumsatz (€)</TableCell>
                      <TableCell align="right">Ø Umsatz/E-Mail (€)</TableCell>
                      <TableCell align="right">Ø Umsatz/Kunde (€)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domainRevenueData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.domain}</TableCell>
                        <TableCell align="right">
                          {row.total_emails.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {row.converted_emails.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {Number(row.conversion_rate).toFixed(2)}%
                        </TableCell>
                        <TableCell align="right">
                          {row.total_orders.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {row.total_revenue?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                        <TableCell align="right">
                          {row.avg_revenue_per_email?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                        <TableCell align="right">
                          {row.avg_revenue_per_customer?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0,00'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Tab 5: CPA-Fenster */}
      <TabPanel value={tabValue} index={4}>
        {!loading && (
          <>
            <Box mb={2} display="flex" alignItems="center" gap={2}>
              <Typography variant="body1" color={colors.grey[100]}>
                Konversionsfenster:
              </Typography>
              <ToggleButtonGroup
                value={cpaWindowDays}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) setCpaWindowDays(value);
                }}
                size="small"
              >
                <ToggleButton value={14}>14 Tage</ToggleButton>
                <ToggleButton value={30}>30 Tage</ToggleButton>
                <ToggleButton value={60}>60 Tage</ToggleButton>
                <ToggleButton value={90}>90 Tage</ToggleButton>
              </ToggleButtonGroup>
              {cpaWindowLoading && <CircularProgress size={20} />}
            </Box>

            <Typography
              variant="body2"
              color={colors.grey[300]}
              mb={2}
              sx={{ fontStyle: 'italic' }}
            >
              Nur Bestellungen innerhalb von {cpaWindowDays} Tagen nach Anmeldung. Kohorten deren
              Fenster noch nicht abgelaufen ist werden ausgeblendet.
            </Typography>

            {/* Summary KPI Cards */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ backgroundColor: colors.primary[400] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[300]}>
                      Ø CPA-Wert / E-Mail
                    </Typography>
                    <Typography variant="h4" color={colors.greenAccent[500]}>
                      €
                      {cpaWindowSummary.avgCpa.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ backgroundColor: colors.primary[400] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[300]}>
                      Σ Anmeldungen
                    </Typography>
                    <Typography variant="h4" color={colors.blueAccent[500]}>
                      {cpaWindowSummary.totalSignups.toLocaleString('de-DE')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ backgroundColor: colors.primary[400] }}>
                  <CardContent>
                    <Typography variant="subtitle2" color={colors.grey[300]}>
                      Σ CPA Gesamt
                    </Typography>
                    <Typography variant="h4" color={colors.redAccent[500]}>
                      €
                      {cpaWindowSummary.totalCpa.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
              <Typography
                variant="h5"
                mb={2}
                color={colors.grey[100]}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                CPA-Wert / E-Mail Entwicklung ({cpaWindowDays}-Tage-Fenster)
                <Tooltip
                  title={
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <strong>Survivorship-Bias-freie Kohortenanalyse</strong>
                      <br />
                      Jeder Monat zeigt nur Bestellungen die innerhalb von {cpaWindowDays} Tagen
                      nach Anmeldung eingegangen sind.
                      <br />
                      So ist jede Kohorte fair vergleichbar — egal ob Jan 2024 oder Dez 2025.
                    </div>
                  }
                >
                  <InfoOutlined
                    sx={{
                      fontSize: 16,
                      ml: 1,
                      color: colors.grey[300],
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              </Typography>
              <Box height="400px" mt={2}>
                {cpaWindowChartData.length > 0 ? (
                  <ResponsiveLine
                    data={cpaWindowChartData}
                    theme={{
                      axis: {
                        domain: { line: { stroke: colors.grey[100] } },
                        legend: { text: { fill: colors.grey[100] } },
                        ticks: {
                          line: { stroke: colors.grey[100], strokeWidth: 1 },
                          text: { fill: colors.grey[100] },
                        },
                      },
                      legends: { text: { fill: colors.grey[100] } },
                      tooltip: { container: { color: colors.primary[500] } },
                    }}
                    colors={[
                      colors.greenAccent[500],
                      'rgba(141, 179, 227, 0.35)',
                      'rgba(180, 100, 100, 0.35)',
                    ]}
                    lineWidth={3}
                    enableArea={true}
                    areaOpacity={0.08}
                    defs={[
                      {
                        id: 'areaGradientCpaW',
                        type: 'linearGradient',
                        colors: [
                          {
                            offset: 0,
                            color: colors.greenAccent[500],
                            opacity: 0.2,
                          },
                          {
                            offset: 100,
                            color: colors.greenAccent[500],
                            opacity: 0,
                          },
                        ],
                      },
                      {
                        id: 'areaHiddenW',
                        type: 'linearGradient',
                        colors: [
                          { offset: 0, color: '#000', opacity: 0 },
                          { offset: 100, color: '#000', opacity: 0 },
                        ],
                      },
                    ]}
                    fill={[
                      {
                        match: { id: 'CPA / E-Mail (€)' },
                        id: 'areaGradientCpaW',
                      },
                      { match: { id: 'Neue Anmeldungen' }, id: 'areaHiddenW' },
                      { match: { id: 'Gesamt CPA (€)' }, id: 'areaHiddenW' },
                    ]}
                    margin={{ top: 50, right: 140, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto',
                      stacked: false,
                      reverse: false,
                    }}
                    tooltip={({ point }) => {
                      const originalValue = (point.data as any).originalValue;
                      const isCurrency =
                        point.serieId === 'CPA / E-Mail (€)' || point.serieId === 'Gesamt CPA (€)';
                      const formattedValue = isCurrency
                        ? `€${originalValue?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : originalValue?.toLocaleString('de-DE');
                      return (
                        <div
                          style={{
                            background: colors.primary[400],
                            padding: '9px 12px',
                            border: `1px solid ${colors.grey[100]}`,
                            borderRadius: '4px',
                            color: colors.grey[100],
                          }}
                        >
                          <strong>{point.serieId}</strong>
                          <br />
                          Monat: {point.data.xFormatted}
                          <br />
                          {isCurrency ? 'Betrag' : 'Anzahl'}: {formattedValue}
                        </div>
                      );
                    }}
                    axisTop={null}
                    axisRight={
                      cpaWindowMaxSignups > 0
                        ? {
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Anmeldungen',
                            legendOffset: 55,
                            legendPosition: 'middle',
                            format: (value: number) => {
                              if (cpaWindowMaxCpaPerSignup === 0) return '0';
                              const denormalized = Math.round(
                                (value / cpaWindowMaxCpaPerSignup) * cpaWindowMaxSignups,
                              );
                              return denormalized.toLocaleString('de-DE');
                            },
                          }
                        : null
                    }
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: 'Anmeldemonat',
                      legendOffset: 45,
                      legendPosition: 'middle',
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: 'CPA / E-Mail (€)',
                      legendOffset: -50,
                      legendPosition: 'middle',
                      format: (value: number) => `€${value}`,
                    }}
                    pointSize={10}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    pointLabelYOffset={-12}
                    useMesh={true}
                    legends={[
                      {
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 130,
                        translateY: 0,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 150,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemBackground: 'rgba(0, 0, 0, .03)',
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                  />
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography color={colors.grey[300]}>
                      Keine Daten für dieses Fenster verfügbar
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Conversion Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h4" color="white">
            Details: {selectedBucket}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2, backgroundColor: colors.primary[500] }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>E-Mail</TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Client
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Anmeldedatum
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Bestelldatum
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Tage bis Konversion
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Produkt
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Status
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Quelle
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Affiliate
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bucketDetails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ color: 'white', py: 3 }}>
                        Keine Daten verfügbar
                      </TableCell>
                    </TableRow>
                  ) : (
                    bucketDetails.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: 'white' }}>{row.email}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: 'white', textTransform: 'uppercase' }}
                        >
                          {row.client || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.signup_date ? moment(row.signup_date).format('DD.MM.YYYY') : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.first_order_date
                            ? moment.utc(row.first_order_date).format('DD.MM.YYYY HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.days_to_convert}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.product_name}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.status || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.source || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.affiliate || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: 'white' }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Conversion Details Dialog */}
      <Dialog
        open={productDetailsDialogOpen}
        onClose={handleCloseProductDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h4" color="white">
            Details: {selectedProduct.name} ({selectedProduct.code})
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingProductDetails ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2, backgroundColor: colors.primary[500] }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>E-Mail</TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Client
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Anmeldedatum
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Bestelldatum
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Tage bis Konversion
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Produkt
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Status
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Quelle
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white' }}>
                      Affiliate
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productDetails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ color: 'white', py: 3 }}>
                        Keine Daten verfügbar
                      </TableCell>
                    </TableRow>
                  ) : (
                    productDetails.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: 'white' }}>{row.email}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: 'white', textTransform: 'uppercase' }}
                        >
                          {row.client || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.signup_date ? moment(row.signup_date).format('DD.MM.YYYY') : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.first_order_date
                            ? moment.utc(row.first_order_date).format('DD.MM.YYYY HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.days_to_convert}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.product_name}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.status || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.source || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white' }}>
                          {row.affiliate || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog} sx={{ color: 'white' }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnfoStatisticsPage;
